import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { PurchaseOrder, CreatePurchaseOrderItemData } from '../../types/purchaseOrder';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { vendorService } from '../../services/vendorService';
import productService, { Product } from '../../services/productService';
import { invoiceTemplateService } from '../../services/invoiceTemplateService';
import { formatCurrency } from '../../utils/formatters';

interface PurchaseOrderFormProps {
  purchaseOrder?: PurchaseOrder;
  onSuccess: () => void;
  onCancel: () => void;
}

interface POItemRow extends CreatePurchaseOrderItemData {
  id?: string;
}

// ─── Design System Tokens ────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
  colorScheme: 'light'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6
};

const sectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 20
};

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#d97706',
  marginBottom: 16,
  paddingBottom: 10,
  borderBottom: '1px solid rgba(92,78,51,0.15)'
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

const smallInputStyle: React.CSSProperties = {
  padding: '6px 8px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
  colorScheme: 'light'
};

const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 8,
  fontWeight: 400,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  whiteSpace: 'nowrap'
};

// ─── Component ───────────────────────────────────────────────────────────────

export const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({
  purchaseOrder,
  onSuccess,
  onCancel
}) => {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    vendorId: purchaseOrder?.vendorId || '',
    templateId: purchaseOrder?.templateId || '',
    orderDate: purchaseOrder?.orderDate
      ? new Date(purchaseOrder.orderDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    expectedDeliveryDate: purchaseOrder?.expectedDeliveryDate
      ? new Date(purchaseOrder.expectedDeliveryDate).toISOString().split('T')[0]
      : '',
    shippingCost: purchaseOrder?.shippingCost || 0,
    notes: purchaseOrder?.notes || '',
    termsAndConditions: purchaseOrder?.termsAndConditions || ''
  });

  const [items, setItems] = useState<POItemRow[]>(
    purchaseOrder?.items?.map(item => ({
      id: item.id,
      productId: item.productId,
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
      taxRate: item.taxRate || 0
    })) || [
      { productId: '', description: '', quantity: 1, rate: 0, taxRate: 0 }
    ]
  );

  // Load vendors
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getVendors({ limit: 1000, status: 'ACTIVE' })
  });

  // Load products
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts({ limit: 1000, isActive: true })
  });

  // Load invoice/document templates (shared with Invoice/Estimate)
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['invoice-templates'],
    queryFn: () => invoiceTemplateService.getAll()
  });

  // Default a new PO to the company's default template once templates load
  React.useEffect(() => {
    if (!purchaseOrder && !formData.templateId && templates && templates.length > 0) {
      const defaultTemplate = templates.find(t => t.isDefault) || templates[0];
      setFormData(prev => ({ ...prev, templateId: defaultTemplate.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: purchaseOrderService.createPurchaseOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.response?.data?.message || 'Failed to create purchase order']);
      setIsSubmitting(false);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      purchaseOrderService.updatePurchaseOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.response?.data?.message || 'Failed to update purchase order']);
      setIsSubmitting(false);
    }
  });

  // Handle product selection
  const handleProductSelect = (index: number, productId: string) => {
    const product = productsData?.data?.find((p: Product) => p.id === productId);
    if (product) {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        productId: product.id,
        description: product.description || product.name,
        rate: product.costPrice || 0,
        taxRate: product.taxCode?.rate || 0
      };
      setItems(newItems);
    }
  };

  // Add item row
  const addItem = () => {
    setItems([...items, { productId: '', description: '', quantity: 1, rate: 0, taxRate: 0 }]);
  };

  // Remove item row
  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Update item field
  const updateItem = (index: number, field: keyof POItemRow, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  // Calculate totals
  const calculateTotals = () => {
    const itemsForCalculation = items.map(item => ({
      quantity: item.quantity,
      rate: item.rate,
      taxRate: item.taxRate ?? 0
    }));
    return purchaseOrderService.calculatePOTotals(itemsForCalculation, formData.shippingCost);
  };

  const totals = calculateTotals();

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setIsSubmitting(true);

    const validationErrors: string[] = [];
    if (!formData.vendorId) validationErrors.push('Please select a vendor');
    if (!formData.orderDate) validationErrors.push('Please enter order date');
    if (items.length === 0) validationErrors.push('Please add at least one item');

    items.forEach((item, index) => {
      if (!item.productId) validationErrors.push(`Item ${index + 1}: Please select a product`);
      if (item.quantity <= 0) validationErrors.push(`Item ${index + 1}: Quantity must be greater than 0`);
      if (item.rate <= 0) validationErrors.push(`Item ${index + 1}: Rate must be greater than 0`);
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    const poData = {
      vendorId: formData.vendorId,
      templateId: formData.templateId || undefined,
      orderDate: formData.orderDate,
      expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
      items: items.map(item => ({
        productId: item.productId,
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        taxRate: item.taxRate || 0
      })),
      shippingCost: formData.shippingCost,
      notes: formData.notes || undefined,
      termsAndConditions: formData.termsAndConditions || undefined
    };

    try {
      if (purchaseOrder) {
        await updateMutation.mutateAsync({ id: purchaseOrder.id, data: poData });
      } else {
        await createMutation.mutateAsync(poData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto',
      zIndex: 50,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 16px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: 1100,
        background: '#ffffff',
        border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8,
        marginBottom: 40,
        overflow: 'hidden'
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid rgba(92,78,51,0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              fontWeight: 500,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: '#6b6a5c',
              marginBottom: 6
            }}>
              Procurement
            </div>
            <h3 style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 22,
              fontWeight: 300,
              color: '#1c2033',
              margin: 0,
              letterSpacing: '0.04em'
            }}>
              {purchaseOrder ? 'Edit Purchase Order' : 'Create Purchase Order'}
            </h3>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: 'transparent',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              color: '#6b6a5c',
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Error Panel ── */}
          {errors.length > 0 && (
            <div style={{
              background: 'rgba(201,90,90,0.08)',
              border: '1px solid rgba(201,90,90,0.2)',
              borderRadius: 4,
              padding: 12
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: 16,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#c95a5a'
              }}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* ── Order Details ── */}
            <div style={sectionStyle}>
              <div style={sectionHeadingStyle}>Order Details</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                {/* Vendor */}
                <div>
                  <label style={labelStyle}>
                    Vendor <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <select
                    value={formData.vendorId}
                    onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                    disabled={isSubmitting || vendorsLoading}
                    style={inputStyle}
                    required
                  >
                    <option value="">
                      {vendorsLoading ? 'Loading vendors...' : 'Select a vendor'}
                    </option>
                    {vendorsData?.data?.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Template */}
                <div>
                  <label style={labelStyle}>Document Template</label>
                  <select
                    value={formData.templateId}
                    onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                    disabled={isSubmitting || templatesLoading}
                    style={inputStyle}
                  >
                    <option value="">
                      {templatesLoading ? 'Loading templates...' : 'Default template'}
                    </option>
                    {templates?.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {template.templateCompanyName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Order Date */}
                <div>
                  <label style={labelStyle}>
                    Order Date <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.orderDate}
                    onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                    disabled={isSubmitting}
                    style={inputStyle}
                    required
                  />
                </div>

                {/* Expected Delivery Date */}
                <div>
                  <label style={labelStyle}>Expected Delivery Date</label>
                  <input
                    type="date"
                    value={formData.expectedDeliveryDate}
                    onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
                    disabled={isSubmitting}
                    min={formData.orderDate}
                    style={inputStyle}
                  />
                </div>

                {/* Shipping Cost */}
                <div>
                  <label style={labelStyle}>Shipping Cost</label>
                  <input
                    type="number"
                    value={formData.shippingCost}
                    onChange={(e) => setFormData({ ...formData, shippingCost: parseFloat(e.target.value) || 0 })}
                    disabled={isSubmitting}
                    step="0.01"
                    min="0"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Line Items ── */}
            <div style={sectionStyle}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                paddingBottom: 10,
                borderBottom: '1px solid rgba(92,78,51,0.15)'
              }}>
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#d97706'
                }}>
                  Line Items
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  disabled={isSubmitting}
                  style={{
                    ...primaryBtnStyle,
                    padding: '6px 14px',
                    fontSize: 9
                  }}
                >
                  + Add Item
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(92,78,51,0.25)' }}>
                      {[
                        { label: 'Product *',   align: 'left'   },
                        { label: 'Description', align: 'left'   },
                        { label: 'Quantity *',  align: 'right'  },
                        { label: 'Rate *',      align: 'right'  },
                        { label: 'Tax %',       align: 'right'  },
                        { label: 'Amount',      align: 'right'  },
                        { label: 'Action',      align: 'center' }
                      ].map(({ label, align }) => (
                        <th key={label} style={{ ...thStyle, textAlign: align as React.CSSProperties['textAlign'] }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const lineTotalCalc = purchaseOrderService.calculateLineTotal(
                        item.quantity,
                        item.rate,
                        item.taxRate || 0
                      );

                      return (
                        <tr key={index} style={{ borderBottom: '1px solid rgba(239,232,213,0.6)' }}>

                          {/* Product */}
                          <td style={{ padding: '6px 10px' }}>
                            <select
                              value={item.productId}
                              onChange={(e) => handleProductSelect(index, e.target.value)}
                              disabled={isSubmitting || productsLoading}
                              style={{ ...smallInputStyle, width: 160 }}
                              required
                            >
                              <option value="">
                                {productsLoading ? 'Loading...' : 'Select product'}
                              </option>
                              {productsData?.data?.map((product: Product) => (
                                <option key={product.id} value={product.id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Description */}
                          <td style={{ padding: '6px 10px' }}>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              disabled={isSubmitting}
                              style={{ ...smallInputStyle, width: 160 }}
                            />
                          </td>

                          {/* Quantity */}
                          <td style={{ padding: '6px 10px' }}>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              disabled={isSubmitting}
                              min="1"
                              step="1"
                              style={{ ...smallInputStyle, width: 68, textAlign: 'right' }}
                              required
                            />
                          </td>

                          {/* Rate */}
                          <td style={{ padding: '6px 10px' }}>
                            <input
                              type="number"
                              value={item.rate}
                              onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                              disabled={isSubmitting}
                              min="0"
                              step="0.01"
                              style={{ ...smallInputStyle, width: 88, textAlign: 'right' }}
                              required
                            />
                          </td>

                          {/* Tax Rate */}
                          <td style={{ padding: '6px 10px' }}>
                            <input
                              type="number"
                              value={item.taxRate || 0}
                              onChange={(e) => updateItem(index, 'taxRate', parseFloat(e.target.value) || 0)}
                              disabled={isSubmitting}
                              min="0"
                              max="100"
                              step="0.01"
                              style={{ ...smallInputStyle, width: 56, textAlign: 'right' }}
                            />
                          </td>

                          {/* Amount */}
                          <td style={{
                            padding: '6px 10px',
                            textAlign: 'right',
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 14,
                            color: '#d97706',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}>
                            {formatCurrency(lineTotalCalc.total)}
                          </td>

                          {/* Remove */}
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              disabled={isSubmitting || items.length === 1}
                              style={{
                                background: 'transparent',
                                border: '1px solid rgba(201,90,90,0.3)',
                                borderRadius: 4,
                                color: '#c95a5a',
                                width: 24,
                                height: 24,
                                cursor: items.length === 1 ? 'not-allowed' : 'pointer',
                                opacity: items.length === 1 ? 0.3 : 1,
                                fontSize: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Totals ── */}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  width: 280,
                  background: '#faf7ee',
                  border: '1px solid rgba(92,78,51,0.15)',
                  borderRadius: 6,
                  padding: '14px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }}>
                  {[
                    ['Subtotal', formatCurrency(totals.subtotal)],
                    ['Tax',      formatCurrency(totals.taxAmount)],
                    ...(formData.shippingCost > 0
                      ? [['Shipping', formatCurrency(formData.shippingCost)]]
                      : [])
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 9,
                        fontWeight: 500,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: '#6b6a5c'
                      }}>
                        {label}
                      </span>
                      <span style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 13,
                        color: '#5b5a52'
                      }}>
                        {value}
                      </span>
                    </div>
                  ))}

                  {/* Grand Total */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: 10,
                    borderTop: '1px solid rgba(92,78,51,0.25)',
                    marginTop: 2
                  }}>
                    <span style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: '#d97706'
                    }}>
                      Total
                    </span>
                    <span style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 22,
                      fontWeight: 300,
                      color: '#d97706'
                    }}>
                      {formatCurrency(totals.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Notes & Terms ── */}
            <div style={sectionStyle}>
              <div style={sectionHeadingStyle}>Notes & Terms</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={isSubmitting}
                    rows={3}
                    placeholder="Internal notes about this purchase order..."
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Terms and Conditions</label>
                  <textarea
                    value={formData.termsAndConditions}
                    onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                    disabled={isSubmitting}
                    rows={3}
                    placeholder="Payment terms, delivery terms, warranties, etc..."
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
                  />
                </div>
              </div>
            </div>

            {/* ── Form Actions ── */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              paddingTop: 8,
              borderTop: '1px solid rgba(92,78,51,0.15)'
            }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                style={ghostBtnStyle}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  ...primaryBtnStyle,
                  opacity: isSubmitting ? 0.5 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting
                  ? 'Saving...'
                  : purchaseOrder
                  ? 'Update Purchase Order'
                  : 'Create Purchase Order'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};
